<template>
  <!--
    Reuses the house ModalSheet, which already follows docs/ION_MODAL_PATTERN.md
    — an ion-modal presented programmatically rather than through :is-open,
    which silently fails to open after mount.
  -->
  <ModalSheet :modelValue="modelValue" @update:modelValue="$emit('update:modelValue', $event)">
    <div class="flex flex-col gap-1 px-5 pb-2 pt-1">
      <div class="text-text-heading-md font-black text-grey-100">Show</div>
      <p class="text-text-body-small mb-2 text-grey-60">
        Collected and expired vouchers are hidden until you ask for them.
      </p>

      <button
        v-for="option in options"
        :key="option.key"
        type="button"
        class="flex min-h-[44px] flex-row items-center gap-3 py-3 text-left transition-transform duration-100 active:scale-[0.99] motion-reduce:transition-none"
        role="switch"
        :aria-checked="String(!!selected[option.key])"
        @click="toggle(option.key)"
      >
        <span
          class="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded border"
          :class="
            selected[option.key] ? 'border-brown-800 bg-brown-800' : 'border-grey-40 bg-white'
          "
        >
          <DsIcon v-if="selected[option.key]" name="check" size="sm" class="text-white" />
        </span>
        <span class="flex-1">
          <span class="text-text-body-md text-grey-100">{{ option.label }}</span>
          <span class="text-text-body-small block text-grey-60">{{ option.count }}</span>
        </span>
      </button>

      <!-- Nothing selected leaves an empty screen with no explanation, so say
           so here where the cause is, rather than in the list. -->
      <div v-if="!anySelected" class="text-text-body-small mt-1 text-status-error">
        Pick at least one to see anything.
      </div>

      <!-- Each tick applies to the list underneath straight away, so there is
           nothing to confirm and nothing to undo — Done only dismisses. A Clear
           all beside it implied the ticks were a draft waiting to be submitted. -->
      <div class="mt-3 pb-2">
        <DsButton
          class="w-full"
          variant="secondary"
          size="48"
          shape="pill"
          @click="$emit('update:modelValue', false)"
        >
          Done
        </DsButton>
      </div>
    </div>
  </ModalSheet>
</template>

<script>
import { Button as DsButton, Icon as DsIcon } from '@team-hogc/hogc-one-design-system';
import ModalSheet from '@/components/common/ModalSheet.vue';

/** Hiding history is the default, so the filter opens already narrowed. */
export const DEFAULT_VOUCHER_FILTER = {
  available: true,
  expiring: true,
  unavailable: true,
  spent: false,
};

export default {
  name: 'VoucherFilterSheet',
  components: { ModalSheet, DsButton, DsIcon },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    selected: {
      type: Object,
      required: true,
    },
    counts: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update:modelValue', 'update:selected'],
  computed: {
    options() {
      return [
        { key: 'available', label: 'Ready to use', count: this.countLabel('available') },
        { key: 'expiring', label: 'Expiring soon', count: this.countLabel('expiring') },
        { key: 'unavailable', label: 'Unavailable', count: this.countLabel('unavailable') },
        { key: 'spent', label: 'Collected & expired', count: this.countLabel('spent') },
      ];
    },
    anySelected() {
      return Object.values(this.selected).some(Boolean);
    },
  },
  methods: {
    countLabel(key) {
      const n = this.counts[key] ?? 0;
      return n === 1 ? '1 voucher' : `${n} vouchers`;
    },
    toggle(key) {
      this.$emit('update:selected', { ...this.selected, [key]: !this.selected[key] });
    },
  },
};
</script>
